// src/pages/Admin/Adherents/DossierInscription.jsx
import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaFileAlt,
  FaGoogleDrive,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { getDossierInscription } from "../../../services/dossierServices";

export default function DossierInscription({ adherentId }) {
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!adherentId) return;
    (async () => {
      try {
        setLoading(true);
        const data = await getDossierInscription(adherentId);
        setDossier(data || null);
      } catch (e) {
        console.error("Erreur DossierInscription:", e);
        setDossier(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [adherentId]);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "Non renseignée";

  const StatusBadge = ({ ok }) => (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold ring-1 ${
        ok
          ? "bg-emerald-100 text-emerald-900 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/50"
          : "bg-rose-100 text-rose-900 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:ring-rose-800/50"
      }`}
    >
      {ok ? <FaCheckCircle /> : <FaTimesCircle />}
      {ok ? "Validé" : "Non validé"}
    </span>
  );

  const LinkButton = ({
    href,
    label = "Ouvrir",
    icon = <FaExternalLinkAlt />,
  }) =>
    href ? (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 ring-1 ring-indigo-300/40 shadow-sm hover:text-white"
      >
        {label} {icon}
      </a>
    ) : (
      <span className="text-xs text-slate-500 dark:text-slate-400">—</span>
    );

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête harmonisé */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Dossier d’inscription
        </h3>
        <div className="mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Contenu */}
      <div className="px-5 pb-6 sm:px-6 sm:pb-6 mt-4">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
              />
            ))}
          </div>
        ) : !dossier ? (
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-700 dark:text-slate-300">
            Aucun dossier trouvé pour cet adhérent.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Certificat médical */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Certificat médical
                  </div>
                  <div className="mt-1 text-sm text-slate-800 dark:text-slate-100">
                    Date : {formatDate(dossier.date_cm)}
                  </div>
                </div>
                <StatusBadge ok={!!dossier.validate_cm} />
              </div>
              <div className="mt-3">
                <LinkButton
                  href={dossier.cm_url}
                  label="Voir le certificat"
                  icon={<FaFileAlt />}
                />
              </div>
            </div>

            {/* Licence Fédérale */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Licence fédérale
                  </div>
                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    Formulaires & validation
                  </div>
                </div>
                <StatusBadge ok={!!dossier.validate_licence} />
              </div>

              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm text-slate-700 dark:text-slate-200">
                    Formulaire à signer
                  </div>
                  <LinkButton
                    href={dossier.formulaire_licence_a_signer}
                    label="Ouvrir"
                    icon={<FaFileAlt />}
                  />
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm text-slate-700 dark:text-slate-200">
                    Formulaire signé
                  </div>
                  <LinkButton
                    href={dossier.formulaire_licence_signe}
                    label="Ouvrir"
                    icon={<FaFileAlt />}
                  />
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm text-slate-700 dark:text-slate-200">
                    Fichier licence
                  </div>
                  <LinkButton
                    href={dossier.licence_url}
                    label="Voir la licence"
                    icon={<FaFileAlt />}
                  />
                </div>
              </div>
            </div>

            {/* Règlement */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Règlement
                </div>
                <StatusBadge ok={!!dossier.validate_reglement} />
              </div>
              <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Statut du règlement pour la saison en cours.
              </div>
            </div>

            {/* Dossier Google Drive */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                Dossier Google Drive
              </div>
              <LinkButton
                href={dossier.lien_drive}
                label="Ouvrir le dossier"
                icon={<FaGoogleDrive />}
              />
              {!dossier.lien_drive && (
                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Aucun lien Drive renseigné.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
